import React, { useRef, useState, useMemo, useCallback } from 'react'
import bannerContext, { BannerParameters } from './bannerContext'
import { noop, uniqueId } from 'utils'

const { Provider } = bannerContext

interface BannersMap {
  [id: string]: Required<
    Pick<BannerParameters, 'message' | 'type' | 'dismissible' | 'onDismiss'>
  >
}

const BannerProvider: React.FC = ({ children }) => {
  const banners = useRef<BannersMap>({})
  const [items, setItems] = useState<string[]>([])

  const dismissBanner = useCallback((id: string) => {
    setItems(items => items.filter(itemId => itemId !== id))

    banners.current[id]?.onDismiss()
    delete banners.current[id]
  }, [])

  const showBanner = useCallback(
    (parameters: BannerParameters) => {
      const {
        message,
        type = 'information',
        id = uniqueId('banner'),
        dismissible = false,
        onDismiss = noop,
      } = parameters

      banners.current[id] = {
        message,
        type,
        dismissible,
        onDismiss,
      }

      setItems(items => [...items, id])

      return {
        id,
        dismissBanner: dismissBanner.bind(null, id),
      }
    },
    [dismissBanner]
  )

  const memoizedValue = useMemo(() => {
    return {
      showBanner,
      dismissBanner,
      items: items.map(id => {
        const { message, type, dismissible } = banners.current[id]

        return {
          id,
          type,
          dismissible,
          message,
        }
      }),
    }
  }, [dismissBanner, items, showBanner])

  return <Provider value={memoizedValue}>{children}</Provider>
}

export default BannerProvider
